import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Box, Button, CircularProgress, Container, Paper, Stack, Typography } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import IosShareRoundedIcon from "@mui/icons-material/IosShareRounded";

import { registerWithPairingCode } from "../../util/push/pushClient";
import {
    checkPushSupport, describeThisDevice, needsAndroidNotificationHint, SUPPORT,
} from "../../util/push/browserSupport";

/**
 * 폰이 QR 로 도달하는 화면 — {@code /m/setup?c=<페어링코드>}.
 *
 * <h3>로그인하지 않는다</h3>
 * <p>코드가 "누구의 구독인지" 만 서버에 알려 준다. 폰에서 사번·비밀번호를 타이핑하지 않는 것이
 * 이 화면의 존재 이유다 — 그게 셋업 마찰의 대부분이었다.
 *
 * <h3>지원되지 않으면 시도조차 하지 않는다</h3>
 * <p>인앱 브라우저(네이버·카카오톡)에는 Push API 가 없고, iOS 는 홈 화면 설치 상태가 아니면
 * 구독이 실패한다. 그냥 시도하게 두면 <b>사용자는 실패한 걸 모른다</b> — [허용]을 눌렀는데
 * 알림이 안 오는 상태가 되고, 설명할 방법이 없다. 그래서 먼저 판별해 무엇을 해야 하는지 알려 준다.
 */
export default function PhoneSetupPage() {
    const location = useLocation();
    const code = useMemo(
        () => new URLSearchParams(location.search).get("c"),
        [location.search]
    );

    const support = useMemo(() => checkPushSupport(), []);
    const [state, setState] = useState("idle");   // idle | working | done | error
    const [message, setMessage] = useState(null);

    const enable = async () => {
        setState("working");
        setMessage(null);
        try {
            await registerWithPairingCode(code);
            setState("done");
        } catch (e) {
            setState("error");
            setMessage(e?.message || "등록에 실패했습니다.");
        }
    };

    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", display: "flex",
                   alignItems: "center", justifyContent: "center", py: 4 }}>
            <Container maxWidth="xs">
                <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #e2e8f0" }}>
                    <Stack alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
                        <Box component="img" src="/pwa/icons/icon-192.png" alt="온앤미"
                             sx={{ width: 56, height: 56 }} />
                        <Typography sx={{ fontSize: 17, fontWeight: 800 }}>휴대폰 알림 받기</Typography>
                        <Typography sx={{ fontSize: 12.5, color: "text.secondary", textAlign: "center" }}>
                            결재·메일·멘션이 오면 잠금화면으로 알려드립니다.
                            <br />내용 확인은 PC 에서 하세요.
                        </Typography>
                    </Stack>

                    {!code && (
                        <Info tone="error">
                            QR 정보가 없습니다. PC 의 알림 설정에서 QR 을 다시 만들어 찍어 주세요.
                        </Info>
                    )}

                    {/* ── 게이트: 지원되지 않는 경로는 여기서 멈춘다 ── */}
                    {code && support === SUPPORT.IN_APP_BROWSER && (
                        <Info tone="error">
                            <b>카카오톡·네이버 앱 안에서는 알림을 켤 수 없습니다.</b>
                            <br />화면 우측 하단(또는 우측 상단) 메뉴에서
                            <b> 다른 브라우저로 열기</b> 를 선택해 주세요.
                            <br />아이폰은 Safari, 안드로이드는 Chrome 입니다.
                        </Info>
                    )}

                    {code && support === SUPPORT.IOS_NEEDS_INSTALL && (
                        <Stack spacing={1.5}>
                            <Info tone="warn">
                                아이폰은 <b>홈 화면에 추가한 뒤에만</b> 알림을 켤 수 있습니다.
                            </Info>
                            <Stack spacing={1}>
                                <Step n={1}>
                                    아래 <IosShareRoundedIcon sx={{ fontSize: 15, verticalAlign: "-3px" }} /> <b>공유</b> 버튼 → <b>홈 화면에 추가</b>
                                    <br/>
                                    혹은 사파리 하단 ... -> 공유 -> 더보기 -> 홈 화면에 추가 하시면 됩니다.
                                </Step>
                                <Step n={2}>
                                    <b>홈 화면에 생긴 온앤미 아이콘</b>을 엽니다
                                    <Typography component="span" sx={{ display: "block", fontSize: 11, color: "text.disabled" }}>
                                        이 QR 페이지가 아니라 <b>그룹웨어 첫 화면</b>이 열립니다. 정상입니다.
                                    </Typography>
                                </Step>
                                <Step n={3}>
                                    <b>로그인</b> 합니다 (이 아이콘에서 처음 한 번만)
                                </Step>
                                <Step n={4}>
                                    화면에서 알람 버튼 클릭 <b>[실시간 알람]</b> → <b>이 기기에서 받기</b> 켜기
                                </Step>
                            </Stack>
                            <Info tone="plain">
                                이 브라우저가 Safari 가 아니면 먼저 Safari 로 QR 을 다시 찍어 주세요.
                            </Info>
                        </Stack>
                    )}

                    {code && support === SUPPORT.UNSUPPORTED && (
                        <Info tone="error">
                            이 브라우저는 알림을 지원하지 않습니다.
                            <br />아이폰은 <b>Safari</b>, 안드로이드는 <b>Chrome</b> 으로 열어 주세요.
                            <br />아이폰은 iOS 16.4 이상이 필요합니다.
                        </Info>
                    )}

                    {/* ── 정상 경로 ── */}
                    {code && support === SUPPORT.OK && state !== "done" && (
                        <Stack spacing={1.5}>
                            <Typography sx={{ fontSize: 12, color: "text.secondary", textAlign: "center" }}>
                                {describeThisDevice()}
                            </Typography>
                            {message && <Info tone="error">{message}</Info>}
                            <Button fullWidth variant="contained" size="large"
                                    onClick={enable} disabled={state === "working"}
                                    sx={{ py: 1.3 }}>
                                {state === "working"
                                    ? <CircularProgress size={20} sx={{ color: "#fff" }} />
                                    : "알림 켜기"}
                            </Button>
                            <Typography sx={{ fontSize: 11, color: "text.disabled", textAlign: "center" }}>
                                버튼을 누르면 알림 권한을 물어봅니다. <b>허용</b> 을 선택하세요.
                            </Typography>
                            {needsAndroidNotificationHint() && (
                                <Info tone="plain">
                                    알림이 오지 않으면 <b>설정 → 앱 → Chrome → 알림</b> 이 켜져 있는지
                                    확인해 주세요. 안드로이드 13 부터는 사이트 권한과 별개입니다.
                                </Info>
                            )}
                        </Stack>
                    )}

                    {state === "done" && (
                        <Stack alignItems="center" spacing={1.5} sx={{ py: 1 }}>
                            <CheckCircleRoundedIcon sx={{ fontSize: 44, color: "#10b981" }} />
                            <Typography sx={{ fontSize: 15, fontWeight: 700 }}>등록 완료</Typography>
                            <Typography sx={{ fontSize: 12.5, color: "text.secondary", textAlign: "center" }}>
                                이제 이 기기로 알림이 옵니다.
                                <br />PC 화면에도 완료 표시가 나타납니다.
                            </Typography>
                            <Info tone="plain">
                                방금 <b>새 기기가 등록되었습니다</b> 알림이 왔다면 정상입니다.
                                본인이 하지 않은 등록이면 PC 의 알림 설정에서 해지하세요.
                            </Info>
                        </Stack>
                    )}
                </Paper>
            </Container>
        </Box>
    );
}

function Step({ n, children }) {
    return (
        <Stack direction="row" spacing={1} alignItems="flex-start">
            <Box sx={{ flex: "none", width: 20, height: 20, borderRadius: "50%", bgcolor: "#EA292C",
                       color: "#fff", fontSize: 11, display: "flex", alignItems: "center",
                       justifyContent: "center", mt: "1px" }}>
                {n}
            </Box>
            <Typography sx={{ fontSize: 12.5, lineHeight: 1.6 }}>{children}</Typography>
        </Stack>
    );
}

function Info({ tone, children }) {
    const palette = {
        warn:  { bg: "#fffbeb", fg: "#92400e" },
        error: { bg: "#fef2f2", fg: "#991b1b" },
        plain: { bg: "#f1f5f9", fg: "#334155" },
    }[tone] || { bg: "#f1f5f9", fg: "#334155" };

    return (
        <Box sx={{ bgcolor: palette.bg, color: palette.fg, borderRadius: 1.5,
                   px: 1.5, py: 1.2, fontSize: 12.5, lineHeight: 1.6 }}>
            {children}
        </Box>
    );
}
